import type { ComponentProps } from 'react';
import { useRouter } from 'next/router';
import { sendPostMessage } from '@/clientOnlyUtils/messagingUtils';
import React, { useMemo } from 'react';

import Button from '@poseidon/components/Input2/Button';
import { Alerts, General } from '@poseidon/components/StrokeIcons';
import { WaveSpinnerContainer } from '@poseidon/components/WaveSpinner';
import { hasItems } from '@poseidon/shared-utils';

import type { PoseidonApiAuthRes } from '~/auth/auth.context';
import { Breadcrumb, Breadcrumbs } from '@poseidon/components/Breadcrumbs';
import { ButtonLink } from '@poseidon/components/Input2';
import { ContentDivider } from '@poseidon/components/ContentDivider';
import PageHeader from '@poseidon/components/PageHeader';
import { useTeamCreateMutation } from '@/hooks/trpc/useTeamMutation';
import { api } from '@/utils/api';
import { useAuthContext } from '~/auth/auth.context';
import { ContentSection } from '@poseidon/components/ContentSection';
import { PaddedScrollable } from '@poseidon/components/Scrollable';
import { TeamsTable, type TeamRowTeam } from '~/components/TeamsTable';

export type PoseidonAuthData = NonNullable<PoseidonApiAuthRes['userTeam']> & {
  impersonation: { token: string; email: string } | null;
};
export type PoseidonSuperAdminAuthData = PoseidonAuthData & { isSuperAdmin: true };

export const AuthedPageWithTeam = (props: { children: (authData: PoseidonAuthData) => React.JSX.Element }) => {
  const [{ isPending, impersonation, ...authData }] = useAuthContext();
  const router = useRouter();

  React.useEffect(() => {
    const routeChangeComplete = (url: string) => {
      sendPostMessage('URL_CHANGE', { url });
    };
    router.events.on('routeChangeComplete', routeChangeComplete);

    return () => {
      router.events.off('routeChangeComplete', routeChangeComplete);
    };
  }, [router]);

  // not logged in, push to login page
  React.useEffect(() => {
    if (!isPending && authData.user == null) {
      const redirectPath = router.asPath.startsWith('/logout') ? undefined : router.asPath;
      void router.push({ pathname: '/login', query: redirectPath ? { redirect: redirectPath } : undefined });
    }
  }, [authData.user, isPending, router]);

  if (isPending) {
    return <WaveSpinnerContainer message="Authenticating" />;
  }

  if (authData.user == null) {
    return <WaveSpinnerContainer message="Not logged in, sending to login page" />;
  }

  if (authData.userTeam != null) {
    return props.children({ ...authData.userTeam, impersonation });
  }

  // user is authenticated but not part of a team
  return <TeamPicker />;
};

type RedirectableProps = ComponentProps<'div'> & {
  redirect?: string;
};

export const SuperAdminAuthedPageWithTeam = (props: {
  children: (authData: PoseidonSuperAdminAuthData) => React.JSX.Element;
}) => {
  const [{ isPending, impersonation, ...authData }] = useAuthContext();
  const router = useRouter();

  // not logged in, push to login page
  React.useEffect(() => {
    if (!isPending && !('userTeam' in authData) && !('user' in authData)) {
      const redirectPath = router.asPath.startsWith('/logout') ? undefined : router.asPath;
      void router.push({ pathname: '/login', query: redirectPath ? { redirect: redirectPath } : undefined });
    }
  }, [authData, isPending, router]);

  // not logged in, push to login page
  React.useEffect(() => {
    if (!isPending && authData.user == null) {
      const redirectPath = router.asPath.startsWith('/logout') ? undefined : router.asPath;
      void router.push({ pathname: '/login', query: redirectPath ? { redirect: redirectPath } : undefined });
    }
  }, [authData.user, isPending, router]);

  if (isPending) {
    return <WaveSpinnerContainer message="Authenticating" />;
  }

  if (authData.user == null) {
    return <WaveSpinnerContainer message="Not logged in, sending to login page" />;
  }

  if (!authData.user.isSuperAdmin) {
    return <WaveSpinnerContainer message="Not Admin" />;
  }

  if (authData.userTeam != null) {
    return props.children({
      ...authData.userTeam,
      isSuperAdmin: authData.user.isSuperAdmin,
      impersonation,
    });
  }

  // user is authenticated but not part of a team
  return <TeamPicker />;
};

/**
 * forces the page to only show up if not logged in
 * redirects out to provided path defined by `redirect` prop or /leeds
 *
 * @param param0
 * @returns
 */
export const WithoutAuthPage = ({ children, redirect }: RedirectableProps) => {
  const [{ user, isPending: isLoading }] = useAuthContext();
  const router = useRouter();

  React.useEffect(() => {
    if (!isLoading && user != null) {
      // adding this execution to another tick to allow other
      // components to change redirect in query after login/signup
      setTimeout(() => {
        const _redirect = redirect || router.query.redirect;
        const redirectRoute =
          _redirect && typeof _redirect === 'string' && _redirect != '/logout' ? _redirect : '/leads';
        void router.push(redirectRoute);
      }, 0);
    }
  }, [user, isLoading, router, redirect]);

  if (isLoading) {
    return <>{children}</>;
  }

  if (user == null) {
    return <>{children}</>;
  } else {
    return null;
  }
};

const TeamPicker = () => {
  const allTeams = api.team.all.useQuery({});
  const teamCreateMutation = useTeamCreateMutation();
  const router = useRouter();

  // Map API response to component's expected interface
  const teams = useMemo<TeamRowTeam[]>(() => {
    if (!allTeams.data) return [];
    return allTeams.data.map((team) => ({
      teamId: team.teamId,
      teamPriceTier: team.teamPriceTier,
      teamName: team.teamName,
      teamLogo: team.teamLogo,
      userTeamStatus: team.userTeamStatus,
      totalUserCount: team.userCounts,
      nImages: team.nUserImages,
    }));
  }, [allTeams.data]);

  if (allTeams.isPending) {
    return <WaveSpinnerContainer message="Loading teams" />;
  }

  const onCreate = async () => {
    await teamCreateMutation.mutateAsync({
      makeDefault: true,
      data: { name: 'My Poseidon Team' },
    });
    void router.push('/teams/active');
  };

  const activeTeams = teams.filter((t) => t.userTeamStatus == 'active' || t.userTeamStatus == 'admin');

  const hasTeams = activeTeams.length > 0;
  return (
    <>
      <PageHeader icon={<General.Settings01 />} title={hasTeams ? 'Please select your team' : 'Please create a team'}>
        <ContentDivider>
          <Breadcrumbs showStartingChevron>
            <Breadcrumb>Teams</Breadcrumb>
          </Breadcrumbs>
        </ContentDivider>
      </PageHeader>

      <PaddedScrollable>
        <ContentSection>
          {hasItems(teams) && <TeamsTable teams={teams} isLoading={allTeams.isPending} />}
          {activeTeams && activeTeams.length <= 0 && <Button onClick={onCreate}>Create a default team</Button>}
        </ContentSection>
        <ContentSection>
          <ContentDivider icon={<Alerts.AlertTriangle />}>Other actions</ContentDivider>
          <div className="flex flex-row items-start gap-2">
            <ButtonLink text icon={<General.LogOut05 />} href="/logout" type="reset">
              Log out
            </ButtonLink>
          </div>
        </ContentSection>
      </PaddedScrollable>
    </>
  );
};
